import { ReactNode, useMemo, useRef } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import useTitle from 'react-use/lib/useTitle';

import DynamicBreadcrumbs from 'src/common/components/Breadcrumbs';
import DisplayHeadingWithRule from 'src/common/components/DisplayHeadingWithRule';
import {
  DashboardPageHeader,
  DashboardPageToolbar,
  DashboardToolbarActions,
  DashboardToolbarAside,
  dashboardPageHeaderTitleTypographySx,
} from 'src/common/components/DashboardPageToolbar';
import DashboardPageScrollContext from 'src/common/components/dashboard/DashboardPageScrollContext';
import {
  dashboardPageFillBodySx,
  dashboardPageRootSx,
  dashboardPageScrollRegionSx,
  dashboardStickyToolbarSx,
} from 'src/common/components/dashboardPageLayout';
import PageBox from 'src/common/components/PageBox';
import { ticketCardGridContainerPaddingSx } from 'src/common/styles/ticketCardStyles';

interface Props {
  children: ReactNode;
  title?: string;
  toolbar?: ReactNode;
  backLink?: { label: string; href: string };
  /** Primary actions aligned with the page title (e.g. Add). */
  headerActions?: ReactNode;
  /** Actions aligned to the right of the toolbar row (e.g. QR scanner). */
  toolbarActions?: ReactNode;
  /** Show loading state in the scrollable content area. */
  isLoading?: boolean;
}

export default function DashboardContentPageLayout({
  children,
  title,
  toolbar,
  backLink,
  headerActions,
  toolbarActions,
  isLoading,
}: Props) {
  const { t } = useTranslation();
  const pageTitle = title ?? t('insights.pageTitle');
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollContextValue = useMemo(() => ({ scrollRef }), [scrollRef]);

  useTitle(`${pageTitle} - ${t('base.defaultTitle')}`);

  const headerTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
      {backLink ? (
        <Tooltip title={backLink.label}>
          <IconButton
            component={RouterLink}
            to={backLink.href}
            aria-label={backLink.label}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
      ) : null}
      <DisplayHeadingWithRule
        variant="h2"
        component="h2"
        sx={{ flex: 1, minWidth: 0 }}
        typographySx={dashboardPageHeaderTitleTypographySx}
      >
        {pageTitle}
      </DisplayHeadingWithRule>
    </Box>
  );

  const showToolbar = Boolean(toolbar || toolbarActions);

  return (
    <DashboardPageScrollContext.Provider value={scrollContextValue}>
      <Box sx={dashboardPageRootSx}>
        <Box ref={scrollRef} sx={dashboardPageScrollRegionSx}>
          <DynamicBreadcrumbs />
          <DashboardPageHeader title={headerTitle} actions={headerActions} />
          {showToolbar ? (
            <Box sx={dashboardStickyToolbarSx}>
              <DashboardPageToolbar edge="sticky">
                {toolbar ? (
                  <DashboardToolbarAside variant="grow">{toolbar}</DashboardToolbarAside>
                ) : null}
                {toolbarActions ? (
                  <DashboardToolbarActions>{toolbarActions}</DashboardToolbarActions>
                ) : null}
              </DashboardPageToolbar>
            </Box>
          ) : null}
          <PageBox
            fill={false}
            isLoading={isLoading}
            sx={
              [
                dashboardPageFillBodySx,
                ticketCardGridContainerPaddingSx,
              ] as SxProps<Theme>
            }
          >
            {children}
          </PageBox>
        </Box>
      </Box>
    </DashboardPageScrollContext.Provider>
  );
}
